import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import { CloudSync, Storage, Settings, Sync } from '@mui/icons-material';
import axios from 'axios';
import { getToken, base_url } from '../../config/config';

const DataSourceToggle = () => {
  const [activeDataSource, setActiveDataSource] = useState(null);
  const [allDataSources, setAllDataSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDataSource, setNewDataSource] = useState({
    name: '',
    sourceType: 'custom_db',
    description: '',
    config: {
      autoSync: false,
      syncInterval: 60
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [activeResponse, allResponse, statsResponse] = await Promise.all([
        axios.get(`${base_url}/v1/stock/datasource/active`, { headers }),
        axios.get(`${base_url}/v1/stock/datasource/all`, { headers }),
        axios.get(`${base_url}/v1/stock/datasource/stats`, { headers })
      ]);

      setActiveDataSource(activeResponse.data.data);
      setAllDataSources(allResponse.data.data);
      setStats(statsResponse.data.data);
    } catch (error) {
      console.error('Error fetching data source information:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchDataSource = async (dataSourceId) => {
    try {
      setSwitching(true);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${base_url}/v1/stock/datasource/switch/${dataSourceId}`, {}, { headers });
      await fetchData();
      setConnectionStatus({ status: 'connected', message: 'Data source switched successfully' });
    } catch (error) {
      console.error('Error switching data source:', error);
      setConnectionStatus({ 
        status: 'error', 
        message: error.response?.data?.message || 'Failed to switch data source' 
      });
    } finally {
      setSwitching(false);
    }
  };

  const testConnection = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${base_url}/v1/stock/datasource/test-connection`, { headers });
      setConnectionStatus(response.data.data);
    } catch (error) {
      setConnectionStatus({
        status: 'failed',
        message: error.response?.data?.message || 'Connection test failed'
      });
    }
  };

  const handleCreateDataSource = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${base_url}/v1/stock/datasource/create`, newDataSource, { headers });
      setCreateDialogOpen(false);
      setNewDataSource({
        name: '',
        sourceType: 'custom_db',
        description: '',
        config: {
          autoSync: false,
          syncInterval: 60
        }
      });
      await fetchData();
    } catch (error) {
      console.error('Error creating data source:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Data Source Management
      </Typography>

      {/* Connection Status Alert */}
      {connectionStatus && (
        <Alert 
          severity={connectionStatus.status === 'connected' ? 'success' : 'error'} 
          sx={{ mb: 2 }}
          onClose={() => setConnectionStatus(null)}
        >
          {connectionStatus.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Current Data Source */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                {activeDataSource?.type === 'odoo' ? <CloudSync /> : <Storage />}
                <Typography variant="h6" ml={1}>
                  Current Data Source
                </Typography>
              </Box>

              {activeDataSource && (
                <>
                  <Typography variant="h5" color="primary" gutterBottom>
                    {activeDataSource.name}
                  </Typography>
                  <Chip 
                    label={activeDataSource.type === 'odoo' ? 'Odoo ERP' : 'Custom Database'} 
                    color="primary" 
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  {activeDataSource.lastSync && (
                    <Typography variant="body2" color="text.secondary">
                      Last Sync: {new Date(activeDataSource.lastSync).toLocaleString()}
                    </Typography>
                  )}
                  
                  <Box mt={2}>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={testConnection}
                      startIcon={<Settings />}
                    >
                      Test Connection
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Statistics
              </Typography>
              
              {stats && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="primary">
                      {stats.products?.total || 0}
                    </Typography>
                    <Typography variant="body2">Total Products</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="secondary">
                      {stats.stockOwners || 0}
                    </Typography>
                    <Typography variant="body2">Stock Owners</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="success.main">
                      {stats.products?.customDb || 0}
                    </Typography>
                    <Typography variant="body2">Custom DB</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="warning.main">
                      {stats.products?.odoo || 0}
                    </Typography>
                    <Typography variant="body2">Odoo Products</Typography>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Available Data Sources */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Available Data Sources
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => setCreateDialogOpen(true)}
                  startIcon={<Settings />}
                >
                  Create New
                </Button>
              </Box>

              <Grid container spacing={2}>
                {allDataSources.map((source) => (
                  <Grid item xs={12} sm={6} md={4} key={source._id}>
                    <Card variant={source.isActive ? "elevation" : "outlined"}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          {source.sourceType === 'odoo' ? <CloudSync /> : <Storage />}
                          <Typography variant="subtitle1" ml={1}>
                            {source.name}
                          </Typography>
                          {source.isActive && (
                            <Chip label="Active" color="primary" size="small" sx={{ ml: 1 }} />
                          )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {source.description || 'No description'}
                        </Typography>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={source.isActive}
                              onChange={() => handleSwitchDataSource(source._id)}
                              disabled={switching || source.isActive}
                            />
                          }
                          label={source.sourceType === 'odoo' ? 'Use Odoo' : 'Use Custom DB'}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Data Source Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Data Source</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={newDataSource.name}
                onChange={(e) => setNewDataSource({ ...newDataSource, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Source Type"
                value={newDataSource.sourceType}
                onChange={(e) => setNewDataSource({ ...newDataSource, sourceType: e.target.value })}
              >
                <MenuItem value="custom_db">Custom Database</MenuItem>
                <MenuItem value="odoo">Odoo ERP</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={newDataSource.description}
                onChange={(e) => setNewDataSource({ ...newDataSource, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newDataSource.config.autoSync}
                    onChange={(e) => setNewDataSource({
                      ...newDataSource,
                      config: { ...newDataSource.config, autoSync: e.target.checked }
                    })}
                  />
                }
                label="Enable Auto Sync"
              />
            </Grid>
            {newDataSource.config.autoSync && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sync Interval (minutes)"
                  value={newDataSource.config.syncInterval}
                  onChange={(e) => setNewDataSource({
                    ...newDataSource,
                    config: { ...newDataSource.config, syncInterval: parseInt(e.target.value) }
                  })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateDataSource} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSourceToggle;